/**
 * In-house powertrain warranty.
 *
 * The Buyers Guide is part of the contract and overrides anything contrary in
 * the purchase agreement, so the term printed there has to be the term sold.
 * The dealer's guide now carries the coverage text in one multiline field,
 * which is why any term can be offered rather than only 90 days.
 */

export interface WarrantyTerm {
  days: number;
  miles: number;
  label: string;
  /** Exactly as it reads on the printed guide. */
  printed: string;
}

export const WARRANTY_TERMS: WarrantyTerm[] = [
  { days: 0,   miles: 0,    label: "Sold as-is", printed: "" },
  { days: 30,  miles: 1000, label: "30 days",    printed: "1 Month / 1,000 Miles" },
  { days: 90,  miles: 3000, label: "90 days",    printed: "3 Months / 3,000 Miles" },
  { days: 180, miles: 6000, label: "180 days",   printed: "6 Months / 6,000 Miles" },
];

/* There is deliberately no default. A form that starts on a warranty term
   gets an as-is car written up with a three-month powertrain warranty the
   moment somebody does not look at that field — and the Buyers Guide is part
   of the contract and overrides the purchase agreement, so the dealership
   then owes a warranty it never meant to sell. The term is chosen per deal,
   every time. 13 October 2026: this caught a real as-is car mid-deal. */

export const termFor = (days: number): WarrantyTerm =>
  WARRANTY_TERMS.find((t) => t.days === days) ?? WARRANTY_TERMS[0]!;

export const isAsIs = (days: number) => !days;

/** As-is sales get the plain FTC guide; warranted sales get the dealer's. */
export const guideForWarranty = (days: number): string =>
  isAsIs(days) ? "buyers_guide_en" : "buyers_guide_warranty";

/**
 * The SYSTEMS COVERED / DURATION block. Measured against the field: 19 lines
 * at 8pt, 176pt of the 232pt available. If this text grows much, check it
 * still fits before shipping — the field does not scroll on paper.
 */
export function warrantyText(days: number, dealerName: string): string {
  const term = termFor(days);
  if (!term.days) return "";

  return [
    `POWERTRAIN WARRANTY — covers the essential parts of the vehicle's propulsion ` +
      `system, including the engine and transmission, and the components that transfer ` +
      `power to the wheels. It does not cover normal wear-and-tear items such as brake ` +
      `pads, or engine accessories such as the alternator, starter or A/C compressor.`,
    "",
    `COVERED COMPONENTS. This is a limited powertrain warranty. Coverage applies only ` +
      `to the components listed below. Any parts, systems or labor not specifically ` +
      `mentioned are not covered.`,
    `Engine: internal lubricated parts including pistons, piston rings, valves, valve ` +
      `springs, cylinder block and cylinder heads, crankshaft and main bearings, ` +
      `camshaft and bearings, connecting rods, oil pump and water pump.`,
    `Transmission: all internal parts of both automatic and manual transmissions, ` +
      `including the torque converter, gears, shafts and clutches (where applicable).`,
    `4WD / AWD: rear axle, front drive axle, transfer case and related internal ` +
      `components. Axle shaft boots (CV boots) are not covered.`,
    "",
    `DURATION: ${term.printed} (whichever occurs first).`,
    "",
    `IMPORTANT. If the vehicle experiences any mechanical failure or warning — ` +
      `including overheating, low oil pressure, coolant or oil leaks, loss of power, ` +
      `transmission shifting problems, or a check engine light — you must immediately ` +
      `and safely stop driving it. Continuing to drive after a mechanical failure is ` +
      `considered neglect and voids this warranty. The vehicle must be towed to our ` +
      `shop for inspection and repair; do not drive it, even a short distance. Towing ` +
      `is not covered. We recommend carrying AAA or roadside assistance on your policy. ` +
      `All warranty repairs must be performed by ${dealerName} unless we authorize ` +
      `otherwise in writing; repairs elsewhere void this warranty.`,
  ].join("\n");
}
